/**
 * Card network identity: one place that decides what a brand string means.
 *
 * Both the face colours and the brand mark key off this, so a network is
 * recognised once rather than in two `includes` chains that can drift apart.
 *
 * The palettes are inspired by each network's own brand colours so a director
 * can tell two saved cards apart instantly, but they are gradients of our own
 * making — not the licensed logos or exact brand assets, which we have no
 * right to reproduce.
 */

/** The networks we draw a distinct face for. */
export const CARD_BRANDS = [
  'visa',
  'mastercard',
  'amex',
  'discover',
  'unionpay',
  'jcb',
  'diners',
  'unknown',
] as const;

export type CardBrand = (typeof CARD_BRANDS)[number];

/**
 * Stripe spells these inconsistently across API versions (`amex` on newer
 * payment methods, `American Express` on legacy sources), so match loosely on a
 * string with case, spaces, underscores and hyphens removed.
 */
const normalise = (brand: string): string => brand.toLowerCase().replace(/[\s_-]/g, '');

/** Ordered because `mastercard` must be tested before a bare `master` style
 *  prefix, and `dinersclub` before `discover` shares no prefix but might. */
const MATCHERS: readonly (readonly [CardBrand, readonly string[]])[] = [
  ['visa', ['visa']],
  ['mastercard', ['mastercard', 'mc']],
  ['amex', ['americanexpress', 'amex']],
  ['discover', ['discover']],
  ['unionpay', ['unionpay', 'cup']],
  ['jcb', ['jcb']],
  ['diners', ['dinersclub', 'diners']],
];

export const cardBrandOf = (brand: string): CardBrand => {
  const key = normalise(brand);
  if (key === '') return 'unknown';

  for (const [id, needles] of MATCHERS) {
    if (needles.some((needle) => key.includes(needle))) return id;
  }

  return 'unknown';
};

interface BrandFace {
  /** Tailwind gradient classes for the card face. */
  gradient: string;
  /** Human-readable network name, for the accessible label. */
  label: string;
}

/**
 * Every face is a dark gradient carrying white text, so contrast never depends
 * on which network a director happens to have saved. Only the hue changes.
 */
export const BRAND_FACES: Record<CardBrand, BrandFace> = {
  visa: {
    gradient: 'from-[#1a1f71] via-[#2a3180] to-[#0d1240]',
    label: 'Visa',
  },
  mastercard: {
    // Deep graphite rather than a literal red/orange: Mastercard's own premium
    // cards are dark, and the logo's colours carry the brand on their own.
    gradient: 'from-[#1c1c22] via-[#2e2b33] to-[#141418]',
    label: 'Mastercard',
  },
  amex: {
    // Deepened from Amex's lighter marketing blue: white type on that hue only
    // reaches 3.6:1, below the 4.5:1 the rest of the app holds to.
    gradient: 'from-[#0a4f78] via-[#0e6494] to-[#052f47]',
    label: 'American Express',
  },
  discover: {
    gradient: 'from-[#4a3411] via-[#8a5f14] to-[#2b1e0a]',
    label: 'Discover',
  },
  unionpay: {
    // A single blue ramp; the logo already carries UnionPay's red and green.
    gradient: 'from-[#0a3f6b] via-[#125a8f] to-[#062537]',
    label: 'UnionPay',
  },
  jcb: {
    gradient: 'from-[#123a6b] via-[#1c4f8a] to-[#0a2340]',
    label: 'JCB',
  },
  diners: {
    gradient: 'from-[#28527a] via-[#33648f] to-[#16324a]',
    label: 'Diners Club',
  },
  // Deliberately neutral: an unrecognised network should look like a card, not
  // like a broken one. Stripe adds networks and we cannot palette them ahead of
  // time.
  unknown: {
    gradient: 'from-[#33383f] via-[#434952] to-[#1f2228]',
    label: 'Card',
  },
};

export const brandFaceOf = (brand: string): BrandFace => BRAND_FACES[cardBrandOf(brand)];
